import type { GeneralInfoPicklistItem } from '../api/generalInfoApi';
import type { AssetSearchUiState } from '../api/assetSearchClient';
import {
  browseFilterLabelForTypeId,
  buildHomeCarouselTypeIdSets,
  buildOfficeVoiceSearchTypeIdSet,
  isHomeGymBrowseTypeSelection,
  normalizeAssetTypeName,
} from '../constants/filterableAssetTypes';
import type { AssistantPriceConstraint } from '../assistant/assistantPriceConstraint';
import type { AssistantSchedule } from '../assistant/assistantSchedule';
import type { FacilityIconId } from './assetFacilityIcons';

function parseOptionalId(raw: string): number | null {
  if (!raw.trim()) {
    return null;
  }
  const n = Number(raw.trim());
  return Number.isFinite(n) ? n : null;
}

/** Map Assets browse filter controls → server search state. */
export function assetSearchUiStateFromBrowseFilters(input: {
  countryId?: string;
  districtId?: string;
  assetTypeId?: string;
  text?: string;
  city?: string | null;
  street?: string | null;
  assetTypeLabel?: string | null;
  countryLabel?: string | null;
  districtLabel?: string | null;
  assetTypes?: GeneralInfoPicklistItem[];
  assetTypesCatalogFull?: GeneralInfoPicklistItem[];
  /** Spoken generic office — match lounge / conference / office-like types together. */
  officeFamily?: boolean;
  open24h?: boolean;
  open24hFacilityIds?: number[] | null;
  amenityIcons?: FacilityIconId[] | null;
  facilityCatalog?: Record<string, unknown>[] | null;
  schedule?: AssistantSchedule | null;
  assetIds?: string[] | null;
  price?: AssistantPriceConstraint | null;
}): AssetSearchUiState {
  const amenityIcons =
    input.amenityIcons?.filter((icon): icon is FacilityIconId => Boolean(icon)) ?? null;
  const state: AssetSearchUiState = {
    countryId: parseOptionalId(input.countryId ?? ''),
    stateId: parseOptionalId(input.districtId ?? ''),
    text: input.text?.trim() || undefined,
    city: input.city?.trim() || null,
    street: input.street?.trim() || null,
    open24h: Boolean(input.open24h),
    open24hFacilityIds:
      input.open24hFacilityIds?.filter((id) => Number.isFinite(id) && id > 0) ?? null,
    amenityIcons: amenityIcons && amenityIcons.length > 0 ? amenityIcons : null,
    facilityCatalog: input.facilityCatalog?.length ? input.facilityCatalog : null,
    schedule: input.schedule ?? null,
    assetIds: input.assetIds?.map((id) => id.trim()).filter(Boolean) ?? null,
    price: input.price ?? null,
    countryLabelLower: input.countryLabel?.trim().toLowerCase() || null,
    stateLabelLower: input.districtLabel?.trim().toLowerCase() || null,
  };

  const catalog = input.assetTypesCatalogFull?.length
    ? input.assetTypesCatalogFull
    : input.assetTypes ?? [];
  state.assetTypesCatalog = catalog.length > 0 ? catalog : undefined;

  const typeRaw = input.assetTypeId?.trim() ?? '';
  const typeId = parseOptionalId(typeRaw);
  if (input.officeFamily) {
    const explicit = input.assetTypeLabel?.trim();
    if (explicit) {
      state.assetTypeLabelLower = normalizeAssetTypeName(explicit);
    }
    const officeIdList = Array.from(buildOfficeVoiceSearchTypeIdSet(catalog));
    if (officeIdList.length > 0) {
      state.assetTypeIds = officeIdList;
    }
    state.assetTypeFamily = 'office';
    return state;
  }
  if (typeId === null) {
    const explicit = input.assetTypeLabel?.trim();
    if (explicit) {
      const labelNorm = normalizeAssetTypeName(explicit);
      state.assetTypeLabelLower = labelNorm;
      if (isHomeGymBrowseTypeSelection(-1, labelNorm)) {
        const { gymIds } = buildHomeCarouselTypeIdSets(catalog);
        const gymIdList = Array.from(gymIds);
        if (gymIdList.length > 0) {
          state.assetTypeIds = gymIdList;
          state.assetTypeFamily = 'gym';
        }
      }
    }
    return state;
  }

  const labelNorm = browseFilterLabelForTypeId(typeId, input.assetTypes ?? [], input.assetTypeLabel);
  state.assetTypeLabelLower = labelNorm;
  if (isHomeGymBrowseTypeSelection(typeId, labelNorm)) {
    const { gymIds } = buildHomeCarouselTypeIdSets(catalog);
    const gymIdList = Array.from(gymIds);
    if (gymIdList.length > 0) {
      state.assetTypeIds = gymIdList;
      state.assetTypeFamily = 'gym';
      return state;
    }
  }

  state.assetTypeId = typeId;
  return state;
}

export function assetSearchUiStateKey(state: AssetSearchUiState): string {
  return JSON.stringify({
    countryId: state.countryId ?? null,
    stateId: state.stateId ?? null,
    assetTypeId: state.assetTypeId ?? null,
    assetTypeIds: state.assetTypeIds ?? null,
    assetTypeFamily: state.assetTypeFamily ?? null,
    text: state.text ?? '',
    city: state.city?.trim() || '',
    street: state.street?.trim() || '',
    open24h: Boolean(state.open24h),
    open24hFacilityIds: state.open24hFacilityIds ?? null,
    amenityIcons: state.amenityIcons ?? null,
    schedule: state.schedule
      ? {
          frequency: state.schedule.frequency,
          weekdays: state.schedule.weekdays,
          closed_weekdays: state.schedule.closed_weekdays,
          start_time: state.schedule.start_time,
          duration_minutes: state.schedule.duration_minutes,
          until_date: state.schedule.until_date,
        }
      : null,
    assetTypeLabelLower: state.assetTypeLabelLower ?? null,
    assetIds: state.assetIds ?? null,
  });
}
